import React, { useState } from "react";
import { useSession } from "hooks/useSession";
import "./RechargeAPI.css";

// ── Payout Navigation for API Partner ──────────────────────────────────────────
const PAYOUT_NAV_ITEMS = [
  { id: "payout_auth", label: "Overview & Auth", color: "#5F5E5A", section: "Getting Started" },
  { id: "payout_initiate", label: "1. Initiate Payout", color: "#3B6D11", section: "Wallet Settlement Flow" },
  { id: "payout_callback", label: "2. Provider Callback", color: "#D85A30", section: null },
  { id: "payout_status", label: "3. Check Status", color: "#BA7517", section: null },
  { id: "payout_lifecycle", label: "Status Lifecycle & Rules", color: "#185FA5", section: "Integration Guide" },
];

// ── General / Legacy Nav Items ────────────────────────────────────────────────
const GENERAL_NAV_ITEMS = [
  { id: "payout_initiate", label: "Initiate Payout", color: "#3B6D11", section: "Payout & Settlement" },
  { id: "payout_callback", label: "Payout Callback", color: "#D85A30", section: null },
  { id: "payout_status", label: "Payout Status Check", color: "#BA7517", section: null },
  { id: "payout_lifecycle", label: "Payout Lifecycle", color: "#185FA5", section: null },
  { id: "auth", label: "Authentication", color: "#5F5E5A", section: "Recharge & Utilities" },
  { id: "get_operator", label: "Fetch Operator", color: "#185FA5", section: null },
  { id: "create_mobile", label: "Create Mobile Recharge", color: "#3B6D11", section: null },
  { id: "recharge_status", label: "Recharge Status Check", color: "#BA7517", section: null },
  { id: "balance", label: "Fetch API Balance", color: "#534AB7", section: "Account" },
];

// ── Shared Styling Tokens ────────────────────────────────────────────────────
const styles = {
  methodBadge: (method) => ({
    display: "inline-block",
    padding: "3px 9px",
    borderRadius: 6,
    fontSize: 11,
    fontWeight: 700,
    fontFamily: "'DM Mono', monospace",
    background: method === "POST" ? "#EAF3DE" : "#E6F1FB",
    color: method === "POST" ? "#2b530c" : "#0C447C",
  }),
  badge: (type) => ({
    display: "inline-block",
    padding: "1px 7px",
    borderRadius: 4,
    fontSize: 10,
    fontWeight: 700,
    background: type === "required" ? "#FCEBEB" : "#F1EFE8",
    color: type === "required" ? "#A32D2D" : "#5F5E5A",
  }),
  codeBlock: {
    background: "#0f172a",
    color: "#e2e8f0",
    border: "1px solid #1e293b",
    borderRadius: 8,
    padding: "14px 16px",
    fontFamily: "'DM Mono', monospace",
    fontSize: 12,
    lineHeight: 1.7,
    overflowX: "auto",
    position: "relative",
    whiteSpace: "pre",
    margin: "10px 0 16px",
  },
  table: { width: "100%", borderCollapse: "collapse", fontSize: 12 },
  th: {
    padding: "9px 12px",
    textAlign: "left",
    background: "#f8fafc",
    color: "#475569",
    fontWeight: 700,
    fontSize: 11,
    borderBottom: "1px solid #e2e8f0",
    textTransform: "uppercase",
    letterSpacing: "0.04em",
  },
  td: { padding: "10px 12px", borderBottom: "1px solid #f1f5f9", verticalAlign: "top" },
  noteBox: (color = "#BA7517") => ({
    background: color === "#BA7517" ? "#FFFBEB" : color === "#185FA5" ? "#EFF6FF" : color === "#3B6D11" ? "#F0FDF4" : "#FEF2F2",
    borderLeft: `4px solid ${color}`,
    borderRadius: "0 8px 8px 0",
    padding: "12px 16px",
    fontSize: 12.5,
    lineHeight: 1.6,
    color: color === "#BA7517" ? "#92400E" : color === "#185FA5" ? "#1E40AF" : color === "#3B6D11" ? "#166534" : "#991B1B",
    marginBottom: 16,
    marginTop: 10,
  }),
};

function CopyButton({ getText }) {
  const [copied, setCopied] = useState(false);
  const copy = () => {
    navigator.clipboard.writeText(getText()).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
  };
  return (
    <button
      type="button"
      onClick={copy}
      style={{
        position: "absolute",
        top: 8,
        right: 8,
        padding: "4px 10px",
        background: "#1e293b",
        border: "1px solid #334155",
        borderRadius: 6,
        fontSize: 11,
        cursor: "pointer",
        color: "#94a3b8",
        fontWeight: 600,
      }}
    >
      {copied ? "✓ Copied" : "Copy"}
    </button>
  );
}

function CodeBlock({ code }) {
  return (
    <div style={styles.codeBlock}>
      <CopyButton getText={() => code} />
      <div>{code}</div>
    </div>
  );
}

function SectionLabel({ children, style = {} }) {
  return (
    <div
      style={{
        fontSize: 11,
        fontWeight: 700,
        color: "#475569",
        letterSpacing: ".06em",
        textTransform: "uppercase",
        marginBottom: 8,
        marginTop: 20,
        ...style,
      }}
    >
      {children}
    </div>
  );
}

function BaseUrl({ method, path, query }) {
  return (
    <div
      style={{
        display: "inline-flex",
        alignItems: "center",
        gap: 10,
        background: "#f8fafc",
        border: "1px solid #e2e8f0",
        borderRadius: 8,
        padding: "8px 14px",
        fontFamily: "'DM Mono', monospace",
        fontSize: 12.5,
        marginTop: 10,
      }}
    >
      <span style={styles.methodBadge(method)}>{method}</span>
      <span style={{ color: "#64748b" }}>https://pro.mokshiq.in/</span>
      <span style={{ color: "#185FA5", fontWeight: 700 }}>{path}</span>
      {query && <span style={{ color: "#94a3b8" }}>{query}</span>}
    </div>
  );
}

function ParamRow({ name, type, required, desc }) {
  return (
    <tr>
      <td style={styles.td}>
        <span style={{ fontFamily: "'DM Mono', monospace", fontSize: 12, color: "#185FA5", fontWeight: 600 }}>
          {name}
        </span>
      </td>
      <td style={styles.td}>
        <span style={{ color: "#64748b", fontFamily: "'DM Mono', monospace", fontSize: 11.5 }}>{type}</span>
      </td>
      <td style={styles.td}>
        <span style={styles.badge(required ? "required" : "optional")}>
          {required ? "Required" : "Optional"}
        </span>
      </td>
      <td style={{ ...styles.td, color: "#334155", lineHeight: 1.5 }}>{desc}</td>
    </tr>
  );
}

function ParamTable({ rows }) {
  return (
    <div style={{ border: "1px solid #e2e8f0", borderRadius: 10, overflow: "hidden", marginBottom: 16 }}>
      <table style={styles.table}>
        <thead>
          <tr>
            <th style={styles.th}>Field</th>
            <th style={styles.th}>Type</th>
            <th style={styles.th}>Required</th>
            <th style={styles.th}>Description / Notes</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((r, i) => (
            <ParamRow key={i} {...r} />
          ))}
        </tbody>
      </table>
    </div>
  );
}

function EndpointHeader({ title, desc, method, path, query }) {
  return (
    <div style={{ marginBottom: 24, paddingBottom: 16, borderBottom: "1px solid #e2e8f0" }}>
      <h2 style={{ fontSize: 22, fontWeight: 700, marginBottom: 6, color: "#0f172a" }}>{title}</h2>
      <p style={{ fontSize: 13.5, color: "#475569", lineHeight: 1.6 }}>{desc}</p>
      <BaseUrl method={method} path={path} query={query} />
    </div>
  );
}

// ════════════════════════════════════════════════════════════════════════════════
// 1. OVERVIEW & AUTHENTICATION
// ════════════════════════════════════════════════════════════════════════════════
function PayoutAuthPage() {
  return (
    <div>
      <div style={{ marginBottom: 24, paddingBottom: 16, borderBottom: "1px solid #e2e8f0" }}>
        <h2 style={{ fontSize: 22, fontWeight: 700, marginBottom: 6, color: "#0f172a" }}>
          Wallet Settlement &amp; Payout API Documentation
        </h2>
        <p style={{ fontSize: 13.5, color: "#475569", lineHeight: 1.6 }}>
          This covers the three core endpoints that make up the wallet-settlement payout flow: 
          <strong> initiating a payout</strong>, the provider's <strong>async callback (webhook)</strong>, and the <strong>manual status-check fallback</strong>.
        </p>
      </div>

      <div style={styles.noteBox("#185FA5")}>
        <strong>Base URL:</strong> <code style={{ fontFamily: "'DM Mono', monospace" }}>https://pro.mokshiq.in</code><br />
        Every authenticated API request must include your active Bearer token in the <code>Authorization</code> header.
      </div>

      <SectionLabel style={{ marginTop: 10 }}>Authentication Headers</SectionLabel>
      <div style={{ border: "1px solid #e2e8f0", borderRadius: 10, overflow: "hidden", marginBottom: 16 }}>
        <table style={styles.table}>
          <thead>
            <tr>
              <th style={styles.th}>Header</th>
              <th style={styles.th}>Value / Format</th>
              <th style={styles.th}>Description</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td style={styles.td}>
                <span style={{ fontFamily: "'DM Mono', monospace", fontSize: 12, color: "#185FA5", fontWeight: 600 }}>
                  Authorization
                </span>
              </td>
              <td style={styles.td}>
                <span style={{ fontFamily: "'DM Mono', monospace", fontSize: 12, color: "#166534" }}>
                  Bearer &lt;YOUR_API_TOKEN&gt;
                </span>
              </td>
              <td style={styles.td}>Bearer token obtained during partner onboarding / login.</td>
            </tr>
            <tr>
              <td style={{ ...styles.td, borderBottom: "none" }}>
                <span style={{ fontFamily: "'DM Mono', monospace", fontSize: 12, color: "#185FA5", fontWeight: 600 }}>
                  Content-Type
                </span>
              </td>
              <td style={{ ...styles.td, borderBottom: "none" }}>
                <span style={{ fontFamily: "'DM Mono', monospace", fontSize: 12, color: "#166534" }}>
                  multipart/form-data
                </span>
              </td>
              <td style={{ ...styles.td, borderBottom: "none" }}>Payout transaction endpoints accept multipart form-data.</td>
            </tr>
          </tbody>
        </table>
      </div>

      <SectionLabel>Three-Pillar Settlement Architecture</SectionLabel>
      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))", gap: 12, margin: "12px 0 20px" }}>
        <div style={{ background: "#fff", border: "1px solid #e2e8f0", borderRadius: 10, padding: 16 }}>
          <div style={{ fontSize: 18, marginBottom: 6 }}>1️⃣</div>
          <strong style={{ fontSize: 13, color: "#0f172a" }}>Initiate Settlement</strong>
          <p style={{ fontSize: 12, color: "#64748b", margin: "6px 0 0" }}>
            Submit payout request with beneficiary bank details. Locks funds and starts processing.
          </p>
        </div>
        <div style={{ background: "#fff", border: "1px solid #e2e8f0", borderRadius: 10, padding: 16 }}>
          <div style={{ fontSize: 18, marginBottom: 6 }}>2️⃣</div>
          <strong style={{ fontSize: 13, color: "#0f172a" }}>Async Callback</strong>
          <p style={{ fontSize: 12, color: "#64748b", margin: "6px 0 0" }}>
            The banking provider calls your webhook once the bank confirms or rejects the transfer.
          </p>
        </div>
        <div style={{ background: "#fff", border: "1px solid #e2e8f0", borderRadius: 10, padding: 16 }}>
          <div style={{ fontSize: 18, marginBottom: 6 }}>3️⃣</div>
          <strong style={{ fontSize: 13, color: "#0f172a" }}>Status Fallback</strong>
          <p style={{ fontSize: 12, color: "#64748b", margin: "6px 0 0" }}>
            Actively poll the provider status for transactions pending confirmation.
          </p>
        </div>
      </div>
    </div>
  );
}

// ════════════════════════════════════════════════════════════════════════════════
// 2. INITIATE WALLET SETTLEMENT
// ════════════════════════════════════════════════════════════════════════════════
function PayoutInitiatePage() {
  return (
    <div>
      <EndpointHeader
        title="1. Initiate Wallet Settlement"
        desc="Moves funds from a partner's main_wallet to a beneficiary bank account via whichever payout provider is currently active (Urban Payout or Softmint — decided server-side by APIManager.code)."
        method="POST"
        path="wallet_settlement_transaction"
      />

      <div style={styles.noteBox("#3B6D11")}>
        <strong>Auth:</strong> Bearer token. Role must be Retailer / API Partner.
      </div>

      <SectionLabel style={{ marginTop: 0 }}>Request — multipart/form-data</SectionLabel>
      <ParamTable
        rows={[
          { name: "transection_mode", type: "string", required: true, desc: "Transfer mode: IMPS, NEFT, or RTGS" },
          { name: "amount", type: "string/number", required: true, desc: "Transfer amount (Min ₹100, Max ₹200,000)" },
          { name: "account_number", type: "string", required: true, desc: "Beneficiary bank account number" },
          { name: "ifsc_code", type: "string", required: true, desc: "Beneficiary bank IFSC code" },
          { name: "holder_name", type: "string", required: true, desc: "Beneficiary account holder name" },
          { name: "remark", type: "string", required: false, desc: "Free-text note (e.g. Retailer settlement)" },
          { name: "lat", type: "string", required: false, desc: "Latitude at time of request (e.g. 28.6139)" },
          { name: "long", type: "string", required: false, desc: "Longitude at time of request (e.g. 77.2090)" },
        ]}
      />

      <SectionLabel>Example Request (curl)</SectionLabel>
      <CodeBlock
        code={`curl -X POST "https://pro.mokshiq.in/wallet_settlement_transaction" \\
  -H "Authorization: Bearer $TOKEN" \\
  -F "transection_mode=IMPS" \\
  -F "amount=15000" \\
  -F "account_number=50100123456789" \\
  -F "ifsc_code=HDFC0001234" \\
  -F "holder_name=Suresh Patel" \\
  -F "remark=Retailer settlement" \\
  -F "lat=28.6139" \\
  -F "long=77.2090"`}
      />

      <SectionLabel>Response — Success (immediate)</SectionLabel>
      <CodeBlock
        code={`{
  "message": "Successfull",
  "status": true,
  "reference_num": "A1B2C3D4"
}`}
      />

      <SectionLabel>Response — Pending (awaiting provider confirmation)</SectionLabel>
      <CodeBlock
        code={`{
  "message": "Transaction Pending!, please wait some time",
  "status": true,
  "reference_num": "A1B2C3D4"
}`}
      />

      <div style={styles.noteBox("#BA7517")}>
        <strong>Important:</strong> <code>status: true</code> here means <em>"request accepted, outcome pending"</em> — not "money delivered." 
        The client must poll <code>/urban_payout_status</code> or wait for the provider callback before treating this as settled.
      </div>

      <SectionLabel>Response — Failed</SectionLabel>
      <CodeBlock
        code={`{
  "message": "Insufficient Amount in your wallet",
  "status": false,
  "reference_num": ""
}`}
      />

      <SectionLabel>Common Failure Messages &amp; Causes</SectionLabel>
      <div style={{ border: "1px solid #e2e8f0", borderRadius: 10, overflow: "hidden", marginBottom: 16 }}>
        <table style={styles.table}>
          <thead>
            <tr>
              <th style={styles.th}>Failure Message</th>
              <th style={styles.th}>Cause / Remediation</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td style={styles.td}><code style={{ color: "#991B1B", fontWeight: 600 }}>"Amount should be less than 200000"</code></td>
              <td style={styles.td}>Amount exceeds maximum single transaction limit (₹2,00,000).</td>
            </tr>
            <tr>
              <td style={styles.td}><code style={{ color: "#991B1B", fontWeight: 600 }}>"Amount should be greater than 100"</code></td>
              <td style={styles.td}>Amount below minimum transaction limit (₹100).</td>
            </tr>
            <tr>
              <td style={styles.td}><code style={{ color: "#991B1B", fontWeight: 600 }}>"Insufficient Amount in your wallet"</code></td>
              <td style={styles.td}>Main wallet balance is lower than amount + transfer charges.</td>
            </tr>
            <tr>
              <td style={styles.td}><code style={{ color: "#991B1B", fontWeight: 600 }}>"Your account has been blocked"</code></td>
              <td style={styles.td}>User profile is inactive or security locked (UserProfile.status != 'active').</td>
            </tr>
            <tr>
              <td style={styles.td}><code style={{ color: "#991B1B", fontWeight: 600 }}>"Payout Service Currently down!"</code></td>
              <td style={styles.td}>Payout API provider gateway disabled (APIManager.status == False).</td>
            </tr>
            <tr>
              <td style={styles.td}><code style={{ color: "#991B1B", fontWeight: 600 }}>"You have invalid scheme assigned, Please contact your admin"</code></td>
              <td style={styles.td}>Commission/charge scheme is not configured or inactive.</td>
            </tr>
            <tr>
              <td style={styles.td}><code style={{ color: "#991B1B", fontWeight: 600 }}>"Payout service is currently down, Please try after some time.."</code></td>
              <td style={styles.td}>Admin float balance below minimum liquidity threshold.</td>
            </tr>
            <tr>
              <td style={styles.td}><code style={{ color: "#991B1B", fontWeight: 600 }}>"Please maintain sufficient amount in your wallet."</code></td>
              <td style={styles.td}>White label partner float balance below minimum threshold.</td>
            </tr>
            <tr>
              <td style={{ ...styles.td, borderBottom: "none" }}>
                <code style={{ color: "#991B1B", fontWeight: 600 }}>"Vendor API timeout. Try again."</code>
              </td>
              <td style={{ ...styles.td, borderBottom: "none" }}>
                Bank/Vendor gateway did not respond within timeout window.
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>
  );
}

// ════════════════════════════════════════════════════════════════════════════════
// 3. PROVIDER CALLBACK (WEBHOOK)
// ════════════════════════════════════════════════════════════════════════════════
function PayoutCallbackPage() {
  return (
    <div>
      <EndpointHeader
        title="2. Provider Callback (Webhook)"
        desc="The payout provider (Urban Payout / Softmint) calls this back-end URL asynchronously once the bank confirms or rejects the transfer. Documented here for backend reconciliation and operational flow."
        method="POST"
        path="urban_payout_callback"
      />

      <div style={styles.noteBox("#185FA5")}>
        <strong>Note:</strong> Cyrus and Softmint have equivalent callback endpoints (<code>cyrus_payout_callback</code>, <code>softmint_payout_callback</code>) sharing the same state reconciliation contract.
      </div>

      <SectionLabel style={{ marginTop: 0 }}>Auth &amp; Security</SectionLabel>
      <div style={styles.noteBox("#BA7517")}>
        <strong>Security &amp; Idempotency:</strong> Providers call this endpoint server-to-server without a Bearer token.
        The backend validates transaction ownership and executes under database row-locks so duplicate callbacks cannot cause double-credit or double-debit.
      </div>

      <SectionLabel>Request from Provider (Urban Payout Shape)</SectionLabel>
      <CodeBlock
        code={`{
  "status": "success",
  "current_status": "success",
  "message": "Transaction completed successfully",
  "transaction_id": "UP998877",
  "client_txn_id": "A1B2C3D4",
  "amount": "15000",
  "utr": "308812345678"
}`}
      />

      <SectionLabel>Response to Provider (Always 200)</SectionLabel>
      <CodeBlock
        code={`{
  "status": true,
  "message": "Successfull",
  "reference_num": "A1B2C3D4"
}`}
      />

      <SectionLabel>Idempotency Guard Response</SectionLabel>
      <p style={{ fontSize: 13, color: "#475569" }}>
        If the referenced Reports row is already in a finalized state (success or failed), the callback returns immediately without re-processing:
      </p>
      <CodeBlock
        code={`{
  "status": false,
  "message": "Transaction already finalized"
}`}
      />

      <SectionLabel>Failure Callback &amp; Auto-Refund</SectionLabel>
      <p style={{ fontSize: 13, color: "#475569" }}>
        When the provider sends a failed notification, the backend responds with failure acknowledgement and <strong>automatically refunds</strong> the previously deducted amount to the retailer's wallet:
      </p>
      <CodeBlock
        code={`// 1. Provider sends:
{
  "status": "failed",
  "message": "Insufficient partner balance",
  "client_txn_id": "A1B2C3D4",
  "amount": "15000"
}

// 2. Backend responds:
{
  "status": false,
  "message": "Transaction Failed",
  "reference_num": "A1B2C3D4"
}`}
      />
    </div>
  );
}

// ════════════════════════════════════════════════════════════════════════════════
// 4. CHECK WALLET SETTLEMENT STATUS
// ════════════════════════════════════════════════════════════════════════════════
function PayoutStatusPage() {
  return (
    <div>
      <EndpointHeader
        title="3. Check Wallet Settlement Status"
        desc="Used when a transaction has been sitting in pending and you want to actively ask the provider for its current state, rather than waiting for the callback."
        method="POST"
        path="urban_payout_status"
      />

      <div style={styles.noteBox("#3B6D11")}>
        <strong>Auth:</strong> Bearer token. Caller must own the transaction (verified via ownership on <code>ref_id</code>).
      </div>

      <SectionLabel style={{ marginTop: 0 }}>Request — multipart/form-data</SectionLabel>
      <ParamTable
        rows={[
          {
            name: "ref_id (or orderid)",
            type: "string",
            required: true,
            desc: "The unique reference number (reference_num) received during payout initiation.",
          },
        ]}
      />

      <SectionLabel>Example Request (curl)</SectionLabel>
      <CodeBlock
        code={`curl -X POST "https://pro.mokshiq.in/urban_payout_status" \\
  -H "Authorization: Bearer $TOKEN" \\
  -F "ref_id=A1B2C3D4"`}
      />

      <SectionLabel>Response — Already Finalized (no API call made)</SectionLabel>
      <CodeBlock
        code={`{
  "status": true,
  "message": "Transaction already finalized",
  "current_status": "success",
  "reference_num": "A1B2C3D4",
  "txn_id": "UP998877",
  "utr": "308812345678",
  "amount": "15000"
}`}
      />

      <SectionLabel>Response — Success (confirmed live from provider)</SectionLabel>
      <CodeBlock
        code={`{
  "status": true,
  "message": "Transaction Successful",
  "current_status": "success",
  "reference_num": "A1B2C3D4",
  "txn_id": "UP998877",
  "utr": "308812345678",
  "amount": "15000",
  "charge": "40"
}`}
      />
      <p style={{ fontSize: 12.5, color: "#64748b" }}>
        Wallet commission distribution and closing balances are applied under a row lock, identically to how the callback applies them — so whichever arrives first (callback or manual check) settles the transaction, and the second one becomes a no-op.
      </p>

      <SectionLabel>Response — Still Pending</SectionLabel>
      <CodeBlock
        code={`{
  "status": false,
  "message": "Transaction Pending",
  "current_status": "pending",
  "reference_num": "A1B2C3D4",
  "txn_id": "UP998877",
  "utr": "",
  "amount": "15000",
  "charge": 0
}`}
      />

      <SectionLabel>Response — Failed (Refund Applied)</SectionLabel>
      <CodeBlock
        code={`{
  "status": false,
  "message": "Transaction Failed",
  "current_status": "failed",
  "reference_num": "A1B2C3D4",
  "txn_id": "UP998877",
  "utr": "",
  "amount": "15000",
  "charge": 0
}`}
      />

      <SectionLabel>Response — Not Found</SectionLabel>
      <CodeBlock
        code={`{
  "status": false,
  "message": "Transaction not found"
}`}
      />
      <div style={styles.noteBox("#5F5E5A")}>
        Returned identically whether the <code>ref_id</code> genuinely doesn't exist or the caller doesn't own it — this avoids confirming to unauthorized callers whether a given ID exists.
      </div>
    </div>
  );
}

// ════════════════════════════════════════════════════════════════════════════════
// 5. STATUS LIFECYCLE & INTEGRATION RULES
// ════════════════════════════════════════════════════════════════════════════════
function PayoutLifecyclePage() {
  return (
    <div>
      <div style={{ marginBottom: 24, paddingBottom: 16, borderBottom: "1px solid #e2e8f0" }}>
        <h2 style={{ fontSize: 22, fontWeight: 700, marginBottom: 6, color: "#0f172a" }}>
          Status Lifecycle &amp; Integration Guidelines
        </h2>
        <p style={{ fontSize: 13.5, color: "#475569", lineHeight: 1.6 }}>
          Comprehensive state machine diagram and critical operational rules for integrating wallet settlements.
        </p>
      </div>

      <SectionLabel style={{ marginTop: 0 }}>State Machine Flowchart</SectionLabel>
      <CodeBlock
        code={`   [Initiate Payout]
          │
          ▼
       pending ──────────────► success  (callback OR manual status-check confirms)
          │                       │
          │                       └─► wallet debited, commission distributed, Reports row closed
          │
          └──────────────► failed    (callback OR manual status-check confirms)
                                 │
                                 └─► amount refunded to wallet automatically`}
      />

      <SectionLabel>Key Integration Rules for Frontend &amp; Operations</SectionLabel>
      <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
        <div style={{ background: "#fff", border: "1px solid #e2e8f0", borderRadius: 10, padding: 16 }}>
          <strong style={{ color: "#0f172a", fontSize: 13.5 }}>
            1. Never treat the initiate call's status: true + "Pending" as final.
          </strong>
          <p style={{ color: "#475569", fontSize: 12.5, margin: "6px 0 0", lineHeight: 1.5 }}>
            Poll <code>/urban_payout_status</code> on an interval (e.g. every 30–60s) for transactions stuck in pending, or rely on the webhook callback if your infrastructure receives it.
          </p>
        </div>

        <div style={{ background: "#fff", border: "1px solid #e2e8f0", borderRadius: 10, padding: 16 }}>
          <strong style={{ color: "#0f172a", fontSize: 13.5 }}>
            2. <code>reference_num</code> is the universal join key across all three endpoints.
          </strong>
          <p style={{ color: "#475569", fontSize: 12.5, margin: "6px 0 0", lineHeight: 1.5 }}>
            Store <code>reference_num</code> against the transaction row in your own database/UI state and use it for every subsequent status check.
          </p>
        </div>

        <div style={{ background: "#fff", border: "1px solid #e2e8f0", borderRadius: 10, padding: 16 }}>
          <strong style={{ color: "#0f172a", fontSize: 13.5 }}>
            3. Idempotency is strictly enforced server-side.
          </strong>
          <p style={{ color: "#475569", fontSize: 12.5, margin: "6px 0 0", lineHeight: 1.5 }}>
            Calling status-check repeatedly, or receiving the callback twice, will never double-credit or double-debit. The second call always returns <em>"Transaction already finalized"</em>.
          </p>
        </div>

        <div style={{ background: "#fff", border: "1px solid #e2e8f0", borderRadius: 10, padding: 16 }}>
          <strong style={{ color: "#0f172a", fontSize: 13.5 }}>
            4. Amount and UTR from the provider are the source of truth.
          </strong>
          <p style={{ color: "#475569", fontSize: 12.5, margin: "6px 0 0", lineHeight: 1.5 }}>
            The UI and reporting ledgers update with the bank UTR (RRN) and verified provider amount once finalized, even if they were unknown at initiation time.
          </p>
        </div>
      </div>
    </div>
  );
}

// ── Legacy Handlers for other roles ───────────────────────────────────────────
function LegacyAuthPage() {
  return <PayoutAuthPage />;
}

// ── Page Map ──────────────────────────────────────────────────────────────────
const PAGE_MAP = {
  payout_auth: <PayoutAuthPage />,
  payout_initiate: <PayoutInitiatePage />,
  payout_callback: <PayoutCallbackPage />,
  payout_status: <PayoutStatusPage />,
  payout_lifecycle: <PayoutLifecyclePage />,
  auth: <LegacyAuthPage />,
};

// ── Main RechargeAPIDocs Component ────────────────────────────────────────────
export default function RechargeAPIDocs() {
  const { role: sessionRole } = useSession();
  const currentRole = sessionRole || localStorage.getItem("role") || "API Partner";
  const roleId = Number(localStorage.getItem("role_id") || 0);
  const roleLower = (currentRole || "").toLowerCase();
  const isAPIPartner =
    roleLower.includes("partner") ||
    roleLower.includes("api") ||
    roleId === 9 ||
    roleId === 18;

  const [activePage, setActivePage] = useState("payout_initiate");
  const navItems = isAPIPartner ? PAYOUT_NAV_ITEMS : GENERAL_NAV_ITEMS;

  return (
    <main>
      <div className="container-fluid p-0">
        <div className="api-doc-container">
          <link
            href="https://fonts.googleapis.com/css2?family=DM+Sans:wght@400;500;600;700&family=DM+Mono:wght@400;500;600&display=swap"
            rel="stylesheet"
          />

          {/* Top Header Bar */}
          <div className="api-doc-header">
            <div className="api-doc-header-left">
              <div className="api-doc-header-logo">
                M
              </div>
              <div>
                <div className="api-doc-header-title">
                  Mokshiq API Documentation
                </div>
                <div className="api-doc-header-sub">
                  Wallet Settlement, Webhook Callback &amp; Status Check · v1.0
                </div>
              </div>
            </div>

            <div className="api-doc-endpoint-badge">
              <span className="api-doc-status-dot" />
              https://pro.mokshiq.in
            </div>
          </div>

          {/* Main Content Area */}
          <div className="api-doc-main">
            {/* Sidebar Navigation */}
            <div className="api-doc-sidebar">
              {navItems.map((item) => (
                <React.Fragment key={item.id}>
                  {item.section && (
                    <div className="api-doc-nav-section">
                      {item.section}
                    </div>
                  )}
                  <div
                    className="api-doc-nav-item"
                    onClick={() => setActivePage(item.id)}
                    style={{
                      borderLeftColor: activePage === item.id ? item.color : "transparent",
                      borderBottomColor: activePage === item.id ? item.color : "transparent",
                      background: activePage === item.id ? `${item.color}14` : "transparent",
                      color: activePage === item.id ? item.color : "#475569",
                      fontWeight: activePage === item.id ? 700 : 500,
                    }}
                  >
                    <span
                      className="api-doc-nav-dot"
                      style={{
                        background: item.color,
                        opacity: activePage === item.id ? 1 : 0.4,
                      }}
                    />
                    {item.label}
                  </div>
                </React.Fragment>
              ))}
            </div>

            {/* Document Body */}
            <div className="api-doc-body">
              {PAGE_MAP[activePage] || PAGE_MAP.payout_initiate}
            </div>
          </div>
        </div>
      </div>
    </main>
  );
}